import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ItemKind, WeaponsResponse } from '../api/types';
import { useI18n } from '../i18n';
import { KindIcon } from './KindIcon';

interface Props {
  data: WeaponsResponse | null;
  activeKind: string | null;
  activeWeapon: string | null;
  /** Lados selecionados em Agentes (CT/TR). */
  activeTeams: string[];
  onSelectKind: (kind: string | null) => void;
  onSelectWeapon: (code: string) => void;
  onSelectTeam: (teams: string[]) => void;
}

/**
 * Barra de categorias em ícones, com um painel que abre ao passar o mouse —
 * o comportamento do buff.163.com.
 *
 * O painel mostra o segundo nível da categoria: as armas (Rifles → AK-47,
 * M4A4…), os subtipos de "Outros" (Adesivos, Pingentes…) ou os lados de
 * "Agentes" (CT/TR). Assim a barra fica curta sem esconder nada.
 *
 * O fechamento tem um pequeno atraso: sem isso, o caminho do mouse entre o
 * ícone e o painel já bastaria para fechá-lo.
 */
const CLOSE_DELAY_MS = 180;

const SEM_HOVER = '(hover: none)';

/** Largura do painel de armas (min-width 240px) mais uma folga de borda. */
const LARGURA_PAINEL = 260;

export function CategoryBar({
  data,
  activeKind,
  activeWeapon,
  activeTeams,
  onSelectKind,
  onSelectWeapon,
  onSelectTeam,
}: Props) {
  const { t, loc } = useI18n();
  const emKits = useLocation().pathname.startsWith('/kits');
  const [open, setOpen] = useState<string | null>(null);
  /**
   * O painel abre ancorado à direita quando não cabe para a direita.
   *
   * Nas últimas categorias da barra ("Agentes", "Outros") ele passava da borda
   * da janela e o documento ganhava uma barra de rolagem horizontal — a página
   * inteira andava para o lado só por passar o mouse. Mesma solução da prévia
   * da peça: mede antes de abrir e vai para o lado em que cabe.
   */
  const [aDireita, setADireita] = useState(false);
  /**
   * Aparelho sem mouse (celular, tablet).
   *
   * Fica em estado e acompanha a media query em vez de ser lido uma vez na
   * montagem: no celular a primeira medida já vem certa, mas num aparelho
   * híbrido (tablet com teclado acoplado) ela muda em pleno uso, e uma leitura
   * congelada deixaria a barra no modo errado até recarregar.
   */
  const [toque, setToque] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(SEM_HOVER).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(SEM_HOVER);
    const muda = (e: MediaQueryListEvent) => setToque(e.matches);
    setToque(mq.matches);
    mq.addEventListener('change', muda);
    return () => mq.removeEventListener('change', muda);
  }, []);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  /** Abre o painel da categoria, decidindo antes para que lado ele cabe. */
  const abrir = (kind: string, alvo: HTMLElement | null) => {
    const slot = alvo?.closest('.catbar-slot');
    const caixa = slot?.getBoundingClientRect();
    // clientWidth, e não innerWidth: descontar a barra de rolagem evita abrir
    // por baixo dela e reintroduzir o transbordo que estamos consertando.
    const limite = document.documentElement.clientWidth;
    setADireita(caixa ? caixa.left + LARGURA_PAINEL > limite : false);
    setOpen(kind);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(null), CLOSE_DELAY_MS);
  };

  useEffect(() => cancelClose, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // No toque não há onMouseLeave para fechar o painel: sem isto ele ficaria
  // aberto por cima da grade até o cliente escolher alguma coisa.
  useEffect(() => {
    if (!open || !toque) return;
    const fora = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [open, toque]);

  const counts = data?.kindCounts ?? {};
  const allKinds = data?.kinds ?? [];
  const hasCounts = Object.keys(counts).length > 0;

  const children = (parent: string): ItemKind[] =>
    allKinds.filter((k) => k.parent === parent && (!hasCounts || (counts[k.key] ?? 0) > 0));

  /** Uma categoria-mãe conta o próprio acervo mais o dos filhos. */
  const totalOf = (kind: ItemKind): number =>
    (counts[kind.key] ?? 0) + children(kind.key).reduce((sum, c) => sum + (counts[c.key] ?? 0), 0);

  // Primeiro nível: só quem não tem mãe, e que tenha algo para mostrar.
  const topKinds = allKinds
    .filter((k) => !k.parent)
    .filter((k) => !hasCounts || totalOf(k) > 0);

  /** A categoria-mãe fica marcada quando um filho dela está selecionado. */
  const isActive = (kind: ItemKind) =>
    activeKind === kind.key || children(kind.key).some((c) => c.key === activeKind);

  return (
    <div className="catbar" ref={rootRef} onMouseLeave={() => !toque && scheduleClose()}>
      <button
        className={`catbar-item${activeKind === null ? ' active' : ''}`}
        onMouseEnter={() => {
          if (toque) return;
          cancelClose();
          setOpen(null);
        }}
        onClick={() => onSelectKind(null)}
      >
        {/* "Todas" tem ícone próprio no painel; sem escolha, cai no do pin. */}
        <KindIcon kind="all" />
        <span className="catbar-label">{t('filters.allFem')}</span>
      </button>

      {/*
        Kits vem logo depois de "Todas": não é uma categoria do catálogo — é
        uma página à parte, com faca e luva já casadas e desconto de par —, mas
        fica na frente das categorias porque é a oferta que a loja quer
        empurrar. "Todas" continua sendo a primeira opção, que é o ponto de
        partida de quem só quer olhar.

        O ícone são as duas peças sobrepostas, montadas com a mesma arte de
        catálogo que as outras categorias usam. Sem catálogo carregado o
        KindIcon cai para 🔪 e 🧤, que dizem a mesma coisa.
      */}
      <Link
        to="/kits"
        className={`catbar-item catbar-kits${emKits ? ' active' : ''}`}
        title="Faca e luva combinando, com desconto"
      >
        <span className="catbar-kits-icone">
          <KindIcon kind="knife" size={26} />
          <KindIcon kind="gloves" size={26} />
        </span>
        <span className="catbar-label">Kits</span>
      </Link>

      {topKinds.map((kind) => {
        const isOpen = open === kind.key;
        const weapons = data?.groups.find((g) => g.kind === kind.key)?.items ?? [];
        const subKinds = children(kind.key);
        // Agentes não têm arma nem subtipo: o segundo nível deles é o lado.
        const teams = kind.key === 'agent' ? data?.agentTeams ?? [] : [];
        const hasPanel = weapons.length > 0 || subKinds.length > 0 || teams.length > 0;
        const total = totalOf(kind);

        return (
          <div
            key={kind.key}
            className="catbar-slot"
            /*
             * No toque este hover não existe.
             *
             * O celular dispara mouseover antes do click, e o React sintetiza
             * onMouseEnter a partir dele: o painel abria aqui, e meio quadro
             * depois o clique via "já está aberto" e navegava direto para a
             * categoria. O segundo nível piscava e sumia. Quem decide no toque
             * é só o onClick do botão.
             */
            onMouseEnter={(e) => {
              if (toque) return;
              cancelClose();
              abrir(kind.key, e.currentTarget);
            }}
          >
            <button
              className={`catbar-item${isActive(kind) ? ' active' : ''}${isOpen ? ' open' : ''}`}
              aria-expanded={hasPanel ? isOpen : undefined}
              onClick={(e) => {
                // No toque, o primeiro clique abre o segundo nível em vez de
                // navegar; o segundo (com o painel já aberto) navega.
                if (toque && hasPanel && !isOpen) {
                  abrir(kind.key, e.currentTarget);
                  return;
                }
                onSelectKind(kind.key);
              }}
            >
              <KindIcon kind={kind.key} />
              <span className="catbar-label">{loc(kind)}</span>
              {total > 0 && <span className="catbar-count">{total}</span>}
            </button>

            {isOpen && hasPanel && (
              <div
                className={`catbar-panel${aDireita ? ' a-direita' : ''}`}
                onMouseEnter={() => !toque && cancelClose()}
                onMouseLeave={() => !toque && scheduleClose()}
              >
                <button
                  className={`catbar-weapon all${
                    activeKind === kind.key && activeWeapon === null && activeTeams.length === 0 ? ' active' : ''
                  }`}
                  onClick={() => {
                    if (teams.length > 0) onSelectTeam([]);
                    else onSelectKind(kind.key);
                    setOpen(null);
                  }}
                >
                  ★ {t('filters.allFem')}
                </button>

                <div className={`catbar-weapons${teams.length > 0 ? ' single' : ''}`}>
                  {/* Agentes: o segundo nível são os lados CT/TR. */}
                  {teams.map((team) => (
                    <button
                      key={team.key}
                      className={`catbar-weapon${activeTeams.includes(team.key) ? ' active' : ''}`}
                      onClick={() => {
                        onSelectTeam([team.key]);
                        setOpen(null);
                      }}
                    >
                      <i className="team-dot" style={{ background: team.key === 'ct' ? '#5e98d9' : '#e0a13c' }} />
                      {loc(team)}
                    </button>
                  ))}

                  {/* Categorias-mãe abrem seus subtipos; armas abrem as armas. */}
                  {subKinds.map((sub) => (
                    <button
                      key={sub.key}
                      className={`catbar-weapon${sub.key === activeKind ? ' active' : ''}`}
                      onClick={() => {
                        onSelectKind(sub.key);
                        setOpen(null);
                      }}
                    >
                      {loc(sub)}
                      {counts[sub.key] ? <span className="catbar-subcount">{counts[sub.key]}</span> : null}
                    </button>
                  ))}

                  {weapons.map((weapon) => (
                    <button
                      key={weapon.code}
                      className={`catbar-weapon${weapon.code === activeWeapon ? ' active' : ''}`}
                      title={weapon.code}
                      onClick={() => {
                        onSelectWeapon(weapon.code);
                        setOpen(null);
                      }}
                    >
                      {weapon.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
